import React from 'react';
import {Button, Table, Tag} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {
    POD_RISK_REVIEW_ACTION_ENABLED,
    PodRiskReviewStatus,
    PodRiskReviewType,
    isPodRiskReviewFinished
} from '../graphql/types/PodRiskReviewType';

interface PodRiskReviewTableProps {
    podRiskReviewData: PodRiskReviewType[];
    loading: boolean;
    /** 打开详情 Drawer（批量审核也由此入口逐条推进） */
    showDetail: (record: PodRiskReviewType) => void;
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: PodRiskReviewType[]) => void;
    selectedRowKeys: React.Key[];
    /** 配送商 ID → 名称；后端不回查该名称，未命中时回落显示 ID */
    dspNameById: Record<string, string>;
    /**
     * 是否显示批量选择复选框。已终止单不可再审核，故「已终止」Tab 不需要选择。
     * 注意：操作列（查看/审核）始终显示，与本开关无关。
     */
    showRowSelection?: boolean;
}

/** 状态对应的 Tag 颜色：待审核中性、不存在风险绿、存在舆情风险红、已取消灰 */
const STATUS_COLOR: Record<PodRiskReviewStatus, string> = {
    [PodRiskReviewStatus.PENDING_REVIEW]: 'processing',
    [PodRiskReviewStatus.APPROVED]: 'success',
    [PodRiskReviewStatus.REJECTED]: 'error',
    [PodRiskReviewStatus.CANCELLED]: 'default'
};

const PodRiskReviewTable: React.FC<PodRiskReviewTableProps> = ({
    podRiskReviewData,
    loading,
    showDetail,
    onSelectChange,
    selectedRowKeys,
    dspNameById,
    showRowSelection = true
}) => {
    const {t} = useTranslation();

    /** 审核结论文案：后端为引擎态，展示语义在前端转换（APPROVED→不存在风险，REJECTED→存在舆情风险） */
    const getStatusText = (status?: PodRiskReviewStatus) =>
        status ? t(`podRiskReview.enum.status.${status}`) : t('podRiskReview.enum.status.UNKNOWN');

    const columns: TableColumnsType<PodRiskReviewType> = [
        {
            title: t('podRiskReview.table.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            render: (text?: string, record?: PodRiskReviewType) => (
                <Button
                    type="link"
                    style={{padding: 0}}
                    onClick={() => record && showDetail(record)}
                >
                    {text || record?.reviewTaskId || '-'}
                </Button>
            )
        },
        {
            title: t('podRiskReview.table.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status?: PodRiskReviewStatus) => (
                <Tag color={status ? STATUS_COLOR[status] : 'default'}>{getStatusText(status)}</Tag>
            )
        },
        {
            title: t('podRiskReview.table.dsp'),
            dataIndex: 'dspId',
            key: 'dspId',
            // 配送商名称后端不回查，用筛选区下拉的 id→name 映射；未命中回落显示 ID
            render: (dspId?: string) => (dspId ? dspNameById[dspId] || dspId : '-')
        },
        {
            title: t('podRiskReview.table.driver'),
            key: 'driver',
            // 后端 DataLoader 回查 account 姓名；查不到回落显示 ID
            render: (_: unknown, record: PodRiskReviewType) =>
                record.driverName || record.driverId || '-'
        },
        {
            title: t('podRiskReview.table.warehouse'),
            key: 'warehouse',
            render: (_: unknown, record: PodRiskReviewType) =>
                record.warehouseName || record.warehouseId || '-'
        },
        {
            title: t('podRiskReview.table.machineHitImageCount'),
            dataIndex: 'machineHitImageCount',
            key: 'machineHitImageCount',
            render: (count?: number) => (count == null ? '-' : count)
        },
        {
            title: t('podRiskReview.table.deliveredAt'),
            key: 'deliveredAt',
            render: (_: unknown, record: PodRiskReviewType) =>
                formatEpochToDateTime(record.deliveredAt)
        },
        {
            // 「创建时间」取 pod_risk_review.created_at，与列表排序字段同源
            title: t('podRiskReview.table.createdAt'),
            key: 'createdAt',
            render: (_: unknown, record: PodRiskReviewType) =>
                formatEpochToDateTime(record.createdAt)
        },
        {
            title: t('podRiskReview.table.reviewer'),
            key: 'reviewer',
            // 待审核单尚无审核人
            render: (_: unknown, record: PodRiskReviewType) =>
                record.reviewer?.name || record.reviewerId || '-'
        },
        {
            title: t('podRiskReview.table.reviewedAt'),
            key: 'reviewedAt',
            // 待审核单尚无审核时间，formatEpochToDateTime 对空值兜底为 '-'
            render: (_: unknown, record: PodRiskReviewType) =>
                formatEpochToDateTime(record.reviewedAt)
        }
    ];

    // 操作列始终存在：已终止单不可再审核，但必须能查看详情（图片 / 日志 / 风险类型）。
    // 按钮文案依「行的实际状态」而非当前 Tab —— 用户可同时用 statuses 筛选，两者未必对应。
    // 审核入口未开放时恒为「查看」，避免点进去却没有提交按钮。
    columns.push({
        title: t('podRiskReview.table.action'),
        key: 'action',
        fixed: 'right' as const,
        render: (_: unknown, record: PodRiskReviewType) => (
            <Button type="link" style={{padding: 0}} onClick={() => showDetail(record)}>
                {POD_RISK_REVIEW_ACTION_ENABLED && !isPodRiskReviewFinished(record.status)
                    ? t('podRiskReview.table.review')
                    : t('podRiskReview.table.view')}
            </Button>
        )
    });

    return (
        <Table<PodRiskReviewType>
            rowKey="reviewTaskId"
            columns={columns}
            dataSource={podRiskReviewData}
            loading={loading}
            pagination={false}
            // max-content + 表头不折行：列宽由内容决定，横向溢出时滚动
            scroll={{x: 'max-content'}}
            className="[&_.ant-table-thead_th]:whitespace-nowrap [&_.ant-table-tbody_td]:whitespace-nowrap"
            rowSelection={
                showRowSelection
                    ? {
                          selectedRowKeys,
                          onChange: onSelectChange
                      }
                    : undefined
            }
        />
    );
};

export default PodRiskReviewTable;
